// Topbar language switcher (WA4 i18n). Drives the Google Website Translate widget (whole-DOM
// translation, en ⇄ hi/or/te/kn/ta/bn) via lib/googleTranslate. The control carries `notranslate`
// so Google never rewrites the menu itself. The choice persists (googtrans cookie) and re-applies
// on every load.
import React, { useEffect } from "react";
import { Menu, MenuItem, MenuSeparator } from "./ui/Menu";
import { TRANSLATE_LANGS, getActiveLang, applyTranslate, verifyTranslation, ensureWidgetLoaded } from "../lib/googleTranslate";
import { notify as toast } from "./ui/useToast";
import { Globe, Check } from "lucide-react";
import { GlobalThemeToggle } from "../design/theme";
import { cn } from "../lib/utils";

/**
 * @param {object} props
 * @param {string} [props.className] - merged onto the trigger button (sizing/position overrides).
 * @param {boolean} [props.compact] - a slightly smaller trigger + icon for tight spaces.
 * @param {boolean} [props.includeThemeToggle] - append a light/dark row below the language list,
 *   for a placement that has no separate room for GlobalThemeToggle beside it (e.g. a floating
 *   mobile trigger) — off by default, so every existing bare `<LanguageSwitcher />` call site is
 *   unchanged.
 * @param {boolean} [props.portal] - render the panel in a portal that flips above the trigger near
 *   the viewport edge, instead of always opening downward — required for a trigger anchored near
 *   the BOTTOM of the viewport (a floating mobile button), where a below-anchored panel would open
 *   off-screen.
 * @param {"left"|"right"} [props.align]
 */
export default function LanguageSwitcher({
  className, compact = false, includeThemeToggle = false, portal = false, align = "right",
}) {
  const active = getActiveLang();
  const current = TRANSLATE_LANGS.find((l) => l.code === active) || TRANSLATE_LANGS[0];
  const isTranslated = active !== "en";

  // If a non-English language was chosen but Google's widget can't load (blocked/offline),
  // the page silently stays English — warn the user once instead of failing silently.
  useEffect(() => verifyTranslation(() =>
    toast.error("Translation is unavailable right now — showing English.")), []);

  const choose = (code) => {
    try { applyTranslate(code); }
    catch { toast.error("Couldn't change the language. Please allow cookies and try again."); }
  };
  return (
    // Warm up the (deferred) Google Translate script on the first hover/focus/tap of the switcher —
    // a language change reloads the page anyway, so this is purely a head start, never a dependency.
    <div className="notranslate" translate="no"
      onPointerEnter={ensureWidgetLoaded} onFocus={ensureWidgetLoaded} onTouchStart={ensureWidgetLoaded}>
      <Menu
        align={align}
        portal={portal}
        aria-label="Language"
        className="min-w-[15rem] p-1"
        button={
          <button
            className={cn(
              "group relative grid place-items-center rounded-2xl border shadow-[0_1px_2px_rgb(15_42_84_/_0.06)] backdrop-blur-md transition-[color,background-color,border-color,box-shadow,transform] duration-200 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              compact ? "w-9 h-9" : "w-10 h-10",
              isTranslated
                ? "border-accent/30 bg-accent/[0.08] text-accent hover:bg-accent/[0.14]"
                : "border-line bg-surface/90 text-ink-2 hover:border-accent/30 hover:bg-accent/5 hover:text-accent",
              className,
            )}
            aria-label={`Language: ${current.label}`}
            data-testid="lang-switcher"
          >
            <Globe
              className={cn(compact ? "w-4 h-4" : "w-[18px] h-[18px]", "transition-transform duration-200 group-hover:rotate-6")}
              aria-hidden="true" strokeWidth={1.9}
            />
            <span className={cn(
              "absolute -bottom-1 -right-1 rounded-full border px-1 text-[9px] font-bold uppercase leading-tight transition-colors",
              isTranslated
                ? "border-accent bg-accent text-white"
                : "border-line bg-surface text-ink-subtle group-hover:border-accent/30 group-hover:text-accent",
            )}>{current.code}</span>
          </button>
        }
      >
        <p className="px-3 pb-1.5 pt-1 text-[11px] font-bold uppercase tracking-[0.16em] text-ink-subtle">
          Language
        </p>
        {TRANSLATE_LANGS.map((l) => {
          const isActive = l.code === active;
          return (
            <MenuItem
              key={l.code}
              onSelect={() => choose(l.code)}
              className={cn("mx-1 w-[calc(100%-0.5rem)] rounded-lg", isActive && "bg-accent/[0.07] hover:bg-accent/[0.1]")}
            >
              <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 py-0.5">
                <span className={cn("text-[14px] font-semibold leading-tight", isActive ? "text-accent" : "text-ink")}>
                  {l.label}
                </span>
                {l.english !== l.label && (
                  <span className="text-[11.5px] leading-tight text-ink-subtle">{l.english}</span>
                )}
              </span>
              {isActive && <Check className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />}
              {isActive && <span className="sr-only"> (selected)</span>}
            </MenuItem>
          );
        })}
        {includeThemeToggle && (
          <>
            <MenuSeparator />
            <div className="px-2 pb-1 pt-0.5">
              <GlobalThemeToggle showLabel className="!w-full !min-w-0 !justify-between !rounded-lg" />
            </div>
          </>
        )}
      </Menu>
    </div>
  );
}
